import { create } from 'zustand';
import { jobAPI } from '../services/api';

const DEFAULT_FILTERS = {
  status: 'all',
  search: '',
  limit: 100,
  offset: 0,
  sortBy: 'created_at',
  sortOrder: 'DESC'
};

const DEFAULT_MATCHING_PREFERENCES = {
  enableImmediateMatching: true,
  immediateMatchLimit: 10,
  minimumScoreThreshold: 50,
  preferredBands: ['A', 'B'],
  autoContactTopMatches: false,
  matchingNotifications: true
};

const DEFAULT_MATCHING_CONFIGURATION = {
  preferences: DEFAULT_MATCHING_PREFERENCES,
  dimensionWeights: [],
  categories: [],
  microPoints: [],
  defaults: {
    preferences: DEFAULT_MATCHING_PREFERENCES,
    dimensionWeights: [],
    categories: [],
    microPoints: []
  },
  source: 'defaults',
  updatedAt: null
};

const asArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    return Array.isArray(parsed) ? parsed : [];
  } catch (_error) {
    return [];
  }
};

const toNumberOrNull = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const toIntegerOrNull = (value) => {
  const number = toNumberOrNull(value);
  return number === null ? null : Math.trunc(number);
};

const getErrorDetails = (error) => {
  const details = error?.details || error?.errors || error?.response?.details || error?.response?.errors;
  return Array.isArray(details) ? details : [];
};

const getErrorDetailMessage = (detail = {}) => detail.message || detail.msg || detail.error || '';

const isGenericValidationMessage = (message) => /^validation failed\.?$/i.test(String(message || '').trim());

const extractError = (error, fallback) => {
  const detailMessage = getErrorDetails(error).map(getErrorDetailMessage).find(Boolean);
  const primaryMessage = error?.response?.message || error?.message || error?.error || error?.response?.error;

  if (detailMessage && isGenericValidationMessage(primaryMessage)) return detailMessage;
  return primaryMessage || detailMessage || fallback;
};

const logJobDocumentJson = (step, payload = {}) => {
  try {
    console.info('JOB_DOCUMENT_UPLOAD_JSON', JSON.stringify({
      event: 'job_document_upload_step',
      step,
      timestamp: new Date().toISOString(),
      ...payload
    }, null, 2));
  } catch (_error) {
    console.info('JOB_DOCUMENT_UPLOAD_JSON', step, payload);
  }
};

const summarizeJobParseResponse = (response = {}) => ({
  requestId: response.requestId || response.metadata?.requestId || null,
  message: response.message || null,
  metadata: response.metadata || null,
  warningCount: response.warnings?.length || 0,
  dataKeys: response.data && typeof response.data === 'object' ? Object.keys(response.data) : [],
  title: response.data?.basicInfo?.title || response.data?.jobDetails?.title || null,
  parsingMethod: response.metadata?.parsingMethod || response.data?.parsingMethod || response.data?.extractionMetadata?.extractionMethod || null,
  confidence: response.metadata?.confidence || response.data?.analysisConfidence || null
});

const cleanParams = (params) => Object.entries(params || {}).reduce((acc, [key, value]) => {
  if (value !== '' && value !== null && value !== undefined) acc[key] = value;
  return acc;
}, {});

const normalizeJobStatus = (status) => {
  if (status === 'inactive') return 'paused';
  return status || 'draft';
};

const matchingSkipMessages = {
  job_has_no_materialized_requirements: 'Matching skipped because this job has no materialized requirements. Complete or re-save the job matching profile, then run matching again.'
};

export const normalizeJob = (job = {}) => {
  const payRange = job.payRange || job.salaryRange || {};
  const positions = job.positions || {};
  const stats = job.stats || {};
  const positionsAvailable = toIntegerOrNull(positions.available ?? job.positionsAvailable ?? job.positions_available) ?? 1;
  const positionsFilled = toIntegerOrNull(positions.filled ?? job.positionsFilled ?? job.positions_filled) ?? 0;
  const processing = job.processing || (job.processing_status ? {
    runId: job.processing_run_id,
    status: job.processing_status,
    stage: job.processing_stage,
    progress: job.processing_progress,
    error: job.processing_error,
    needsReview: job.needs_review
  } : null);

  return {
    ...job,
    id: job.id,
    title: job.title || '',
    description: job.description || '',
    requirementsText: job.requirementsText || job.requirements_text || '',
    requirements_text: job.requirementsText || job.requirements_text || '',
    jobType: job.jobType || job.job_type || '',
    job_type: job.jobType || job.job_type || '',
    experienceLevel: job.experienceLevel || job.experience_level || '',
    experience_level: job.experienceLevel || job.experience_level || '',
    location: job.location || '',
    isRemoteAllowed: Boolean(job.isRemoteAllowed ?? job.is_remote_allowed),
    is_remote_allowed: Boolean(job.isRemoteAllowed ?? job.is_remote_allowed),
    payRange: {
      min: toNumberOrNull(payRange.min ?? job.payRangeMin ?? job.pay_range_min),
      max: toNumberOrNull(payRange.max ?? job.payRangeMax ?? job.pay_range_max),
      currency: 'USD',
      period: payRange.period || job.payPeriod || job.pay_period || null,
      source: payRange.source || null,
      conversion: payRange.conversion || null
    },
    pay_range_min: toNumberOrNull(payRange.min ?? job.payRangeMin ?? job.pay_range_min),
    pay_range_max: toNumberOrNull(payRange.max ?? job.payRangeMax ?? job.pay_range_max),
    pay_currency: 'USD',
    pay_period: payRange.period || job.payPeriod || job.pay_period || null,
    positionsAvailable,
    positionsFilled,
    positionsRemaining: toIntegerOrNull(positions.remaining ?? job.positionsRemaining ?? job.positions_remaining) ?? Math.max(positionsAvailable - positionsFilled, 0),
    positions_available: positionsAvailable,
    positions_filled: positionsFilled,
    contractDurationMonths: toIntegerOrNull(job.contractDurationMonths ?? job.contract_duration_months),
    contract_duration_months: toIntegerOrNull(job.contractDurationMonths ?? job.contract_duration_months),
    targetStartDate: job.targetStartDate || job.target_start_date || '',
    target_start_date: job.targetStartDate || job.target_start_date || '',
    matchBandPreference: job.matchBandPreference || job.match_band_preference || 'A',
    match_band_preference: job.matchBandPreference || job.match_band_preference || 'A',
    nonNegotiableFilters: asArray(job.nonNegotiableFilters ?? job.non_negotiable_filters),
    non_negotiable_filters: asArray(job.nonNegotiableFilters ?? job.non_negotiable_filters),
    status: normalizeJobStatus(job.status),
    completionPercentage: toNumberOrNull(job.completionPercentage ?? job.completion_percentage) ?? 0,
    isComplete: Boolean(job.isComplete ?? job.is_complete),
    postingMethod: job.postingMethod || job.posting_method || 'manual',
    processing: processing ? {
      ...processing,
      runId: processing.runId || processing.id,
      progress: Number(processing.progress || 0),
      needsReview: Boolean(processing.needsReview)
    } : null,
    companyName: job.companyName || job.company_name || '',
    createdAt: job.createdAt || job.created_at,
    updatedAt: job.updatedAt || job.updated_at,
    publishedAt: job.publishedAt || job.published_at,
    expiresAt: job.expiresAt || job.expires_at,
    stats: {
      totalMatches: Number(stats.totalMatches ?? job.totalMatches ?? 0) || 0,
      mutualMatches: Number(stats.mutualMatches ?? job.mutualMatches ?? 0) || 0,
      employerInterested: Number(stats.employerInterested ?? 0) || 0,
      candidateInterested: Number(stats.candidateInterested ?? 0) || 0,
      offersSent: Number(stats.offersSent ?? 0) || 0,
      offersAccepted: Number(stats.offersAccepted ?? 0) || 0,
      averageMatchScore: toNumberOrNull(stats.averageMatchScore ?? job.averageMatchScore),
      latestMatchDate: stats.latestMatchDate || job.latestMatchDate
    }
  };
};

const normalizeJobDetails = (response = {}) => ({
  job: normalizeJob(response.job || response.data || response),
  requirements: asArray(response.requirements),
  fieldStatus: response.fieldStatus || {},
  stats: response.stats || {},
  aiParsedData: response.aiParsedData || null
});

const normalizeMatchesResponse = (response = {}) => {
  const matches = response.matching?.topMatches || response.matches || response.data || [];
  const summary = response.summary || response.matching?.summary || {};
  const matchingRun = response.matchingRun || response.matching || {};
  const warning = matchingRun.warning ||
    response.matching?.warning ||
    matchingRun.error ||
    response.matching?.error ||
    matchingSkipMessages[matchingRun.reason] ||
    matchingRun.reason;

  return {
    job: response.job ? normalizeJob(response.job) : null,
    matches: asArray(matches),
    summary,
    pagination: response.pagination || {
      total: asArray(matches).length,
      limit: asArray(matches).length,
      offset: 0,
      hasMore: Boolean(matchingRun.hasMoreMatches)
    },
    statistics: {
      candidatesEvaluated: Number(matchingRun.candidatesEvaluated ?? matchingRun.matchesCreated ?? summary.totalMatches ?? 0) || 0,
      matchesCreated: Number(matchingRun.matchesCreated ?? summary.totalMatches ?? asArray(matches).length) || 0,
      hasMoreMatches: Boolean(matchingRun.hasMoreMatches ?? response.pagination?.hasMore),
      queued: Boolean(matchingRun.queued),
      skipped: Boolean(matchingRun.skipped),
      reason: matchingRun.reason || null,
      warning
    },
    displayPolicy: response.displayPolicy || matchingRun.displayPolicy || null
  };
};

const normalizeMatchingConfiguration = (response = {}) => {
  const configuration = response.configuration || response;
  const defaults = configuration.defaults || {};

  return {
    ...DEFAULT_MATCHING_CONFIGURATION,
    ...configuration,
    preferences: {
      ...DEFAULT_MATCHING_PREFERENCES,
      ...(configuration.preferences || {})
    },
    dimensionWeights: asArray(configuration.dimensionWeights),
    categories: asArray(configuration.categories),
    microPoints: asArray(configuration.microPoints),
    defaults: {
      preferences: {
        ...DEFAULT_MATCHING_PREFERENCES,
        ...(defaults.preferences || {})
      },
      dimensionWeights: asArray(defaults.dimensionWeights),
      categories: asArray(defaults.categories),
      microPoints: asArray(defaults.microPoints)
    }
  };
};

const toJobPayload = (jobData = {}, options = {}) => {
  const payRange = jobData.payRange || {};
  const payload = cleanParams({
    title: jobData.title,
    description: jobData.description,
    requirementsText: jobData.requirementsText ?? jobData.requirements_text,
    jobType: jobData.jobType ?? jobData.job_type,
    experienceLevel: jobData.experienceLevel ?? jobData.experience_level,
    location: jobData.location,
    isRemoteAllowed: jobData.isRemoteAllowed ?? jobData.is_remote_allowed,
    payRangeMin: toNumberOrNull(jobData.payRangeMin ?? jobData.pay_range_min ?? payRange.min),
    payRangeMax: toNumberOrNull(jobData.payRangeMax ?? jobData.pay_range_max ?? payRange.max),
    payCurrency: 'USD',
    payPeriod: jobData.payPeriod ?? jobData.pay_period ?? payRange.period,
    contractDurationMonths: toIntegerOrNull(jobData.contractDurationMonths ?? jobData.contract_duration_months),
    positionsAvailable: toIntegerOrNull(jobData.positionsAvailable ?? jobData.positions_available) || 1,
    targetStartDate: jobData.targetStartDate ?? jobData.target_start_date,
    matchBandPreference: jobData.matchBandPreference ?? jobData.match_band_preference ?? 'A',
    nonNegotiableFilters: asArray(jobData.nonNegotiableFilters ?? jobData.non_negotiable_filters)
  });

  if (options.includeCreateFields !== false) {
    payload.postingMethod = jobData.postingMethod || 'manual';
    payload.autoPublish = Boolean(jobData.autoPublish);
  }

  if (payload.isRemoteAllowed !== undefined) {
    payload.isRemoteAllowed = Boolean(payload.isRemoteAllowed);
  }

  return payload;
};

const updateMatchInList = (matches, matchId, updater) => matches.map((match) => (
  String(match.matchId) === String(matchId) ? updater(match) : match
));

const applySectionFields = (job, fields = {}) => {
  if (!job) return job;
  const hasCompensation = ['payRangeMin', 'payRangeMax', 'payCurrency', 'payPeriod'].some(field => fields[field] !== undefined);
  return {
    ...job,
    ...fields,
    payRange: hasCompensation ? {
      ...(job.payRange || {}),
      min: fields.payRangeMin !== undefined ? fields.payRangeMin : job.payRange?.min,
      max: fields.payRangeMax !== undefined ? fields.payRangeMax : job.payRange?.max,
      currency: 'USD',
      period: fields.payPeriod !== undefined ? fields.payPeriod : job.payRange?.period
    } : job.payRange
  };
};

const useJobStore = create((set, get) => ({
  jobs: [],
  currentJob: null,
  currentJobDetails: null,
  jobRequirements: [],
  fieldStatus: {},
  jobStats: {},
  aiParsedData: null,
  matchingCandidates: [],
  immediateMatches: [],
  matchesSummary: null,
  matchesPagination: null,
  matchesDisplayPolicy: null,
  jobProfileSetup: null,
  jobAnalytics: null,
  matchingPreferences: DEFAULT_MATCHING_PREFERENCES,
  matchingConfiguration: DEFAULT_MATCHING_CONFIGURATION,
  jobFilters: DEFAULT_FILTERS,
  pagination: null,
  summary: null,
  loading: false,
  jobsLoading: false,
  configurationLoading: false,
  analyticsLoading: false,
  error: null,
  lastMatchingResults: null,

  upsertJob: (jobData) => {
    const job = normalizeJob(jobData);
    set(state => ({
      jobs: [job, ...state.jobs.filter(item => String(item.id) !== String(job.id))],
      currentJob: state.currentJob && String(state.currentJob.id) === String(job.id)
        ? { ...state.currentJob, ...job }
        : state.currentJob
    }));
    return job;
  },

  applyJobProcessing: (jobId, processing) => set(state => ({
    jobs: state.jobs.map(job => String(job.id) === String(jobId) ? {
      ...job,
      processing: {
        ...(job.processing || {}),
        ...processing,
        runId: processing?.id || processing?.runId,
        progress: Number(processing?.progress || 0)
      }
    } : job),
    currentJob: state.currentJob && String(state.currentJob.id) === String(jobId) ? {
      ...state.currentJob,
      processing: {
        ...(state.currentJob.processing || {}),
        ...processing,
        runId: processing?.id || processing?.runId,
        progress: Number(processing?.progress || 0)
      }
    } : state.currentJob
  })),

  applyJobSection: (jobId, fields) => set(state => ({
    jobs: state.jobs.map(job => String(job.id) === String(jobId) ? applySectionFields(job, fields) : job),
    currentJob: state.currentJob && String(state.currentJob.id) === String(jobId)
      ? applySectionFields(state.currentJob, fields)
      : state.currentJob
  })),

  fetchEmployerJobs: async (filters = {}) => {
    set({ loading: true, jobsLoading: true, error: null });
    try {
      const mergedFilters = cleanParams({ ...get().jobFilters, ...filters });
      const response = await jobAPI.getEmployerJobs(mergedFilters);
      const jobs = asArray(response.jobs || response.data || response).map(normalizeJob);
      set({
        jobs,
        pagination: response.pagination || null,
        summary: response.summary || null,
        loading: false,
        jobsLoading: false,
        error: null
      });
      return { success: true, jobs, pagination: response.pagination, summary: response.summary };
    } catch (error) {
      const errorMessage = extractError(error, 'Failed to fetch jobs');
      set({ loading: false, jobsLoading: false, error: errorMessage });
      return { success: false, error: errorMessage };
    }
  },

  fetchJobs: async (filters = {}) => get().fetchEmployerJobs(filters),

  createJob: async (jobData) => {
    set({ loading: true, error: null });
    try {
      const response = await jobAPI.create(toJobPayload(jobData));
      const job = normalizeJob(response.job || response.data || response);
      set(state => ({
        jobs: [job, ...state.jobs.filter(item => String(item.id) !== String(job.id))],
        currentJob: job,
        loading: false,
        error: null
      }));
      return { success: true, job, response };
    } catch (error) {
      const errorMessage = extractError(error, 'Failed to create job');
      set({ loading: false, error: errorMessage });
      return {
        success: false,
        error: error?.code === 'EMPLOYER_PROFILE_REQUIRED' ? 'EMPLOYER_PROFILE_REQUIRED' : errorMessage,
        code: error?.code,
        details: getErrorDetails(error)
      };
    }
  },

  parseJobDocument: async (file) => {
    set({ loading: true, error: null });
    logJobDocumentJson('store_parse_started', {
      file: {
        name: file?.name || null,
        type: file?.type || null,
        size: file?.size || 0,
        lastModified: file?.lastModified || null
      }
    });
    try {
      const response = await jobAPI.parseJobDescription(file);
      set({ loading: false, error: null });
      logJobDocumentJson('store_parse_succeeded', summarizeJobParseResponse(response));
      return { success: true, data: response.data || response, metadata: response.metadata, warnings: response.warnings || [] };
    } catch (error) {
      const errorMessage = extractError(error, 'Failed to parse document');
      set({ loading: false, error: errorMessage });
      logJobDocumentJson('store_parse_failed', {
        error: errorMessage,
        code: error?.code || null,
        status: error?.status || null,
        response: error?.response || null
      });
      return { success: false, error: errorMessage };
    }
  },

  fetchJobById: async (jobId) => {
    set({ loading: true, error: null });
    try {
      const response = await jobAPI.getById(jobId);
      const details = normalizeJobDetails(response);
      set({
        currentJob: details.job,
        currentJobDetails: details,
        jobRequirements: details.requirements,
        fieldStatus: details.fieldStatus,
        jobStats: details.stats,
        aiParsedData: details.aiParsedData,
        loading: false,
        error: null
      });
      return { success: true, ...details };
    } catch (error) {
      const errorMessage = extractError(error, 'Failed to fetch job details');
      set({ loading: false, error: errorMessage, currentJob: null, currentJobDetails: null });
      return { success: false, error: errorMessage };
    }
  },

  updateJob: async (jobId, jobData) => {
    set({ loading: true, error: null });
    try {
      const response = await jobAPI.update(jobId, toJobPayload(jobData, { includeCreateFields: false }));
      const updatedJob = normalizeJob(response.job || response.data || { ...jobData, id: jobId });
      set(state => ({
        jobs: state.jobs.map(job => String(job.id) === String(jobId) ? { ...job, ...updatedJob } : job),
        currentJob: state.currentJob && String(state.currentJob.id) === String(jobId) ? { ...state.currentJob, ...updatedJob } : state.currentJob,
        loading: false,
        error: null
      }));
      return { success: true, job: updatedJob, response };
    } catch (error) {
      const errorMessage = extractError(error, 'Failed to update job');
      set({ loading: false, error: errorMessage });
      return { success: false, error: errorMessage };
    }
  },

  deleteJob: async (jobId) => {
    set({ loading: true, error: null });
    try {
      const response = await jobAPI.delete(jobId);
      set(state => ({
        jobs: state.jobs.filter(job => String(job.id) !== String(jobId)),
        currentJob: state.currentJob && String(state.currentJob.id) === String(jobId) ? null : state.currentJob,
        loading: false,
        error: null
      }));
      return { success: true, response };
    } catch (error) {
      const errorMessage = extractError(error, 'Failed to delete job');
      set({ loading: false, error: errorMessage });
      return { success: false, error: errorMessage };
    }
  },

  publishJob: async (jobId) => {
    set({ loading: true, error: null });
    try {
      const response = await jobAPI.publish(jobId);
      const job = normalizeJob(response.job || { id: jobId, status: 'active' });
      const matchResponse = normalizeMatchesResponse(response);
      set(state => ({
        jobs: state.jobs.map(item => String(item.id) === String(jobId) ? { ...item, ...job, status: 'active' } : item),
        currentJob: state.currentJob && String(state.currentJob.id) === String(jobId) ? { ...state.currentJob, ...job, status: 'active' } : state.currentJob,
        immediateMatches: matchResponse.matches,
        lastMatchingResults: matchResponse.statistics,
        matchesDisplayPolicy: matchResponse.displayPolicy,
        loading: false,
        error: null
      }));
      return { success: true, job, immediateMatches: matchResponse.matches, matchingResults: matchResponse.statistics, response };
    } catch (error) {
      const errorMessage = extractError(error, 'Failed to publish job');
      set({ loading: false, error: errorMessage });
      return { success: false, error: errorMessage };
    }
  },

  unpublishJob: async (jobId) => {
    set({ loading: true, error: null });
    try {
      const response = await jobAPI.unpublish(jobId);
      const job = normalizeJob(response.job || { id: jobId, status: 'paused' });
      set(state => ({
        jobs: state.jobs.map(item => String(item.id) === String(jobId) ? { ...item, ...job, status: 'paused' } : item),
        currentJob: state.currentJob && String(state.currentJob.id) === String(jobId) ? { ...state.currentJob, ...job, status: 'paused' } : state.currentJob,
        loading: false,
        error: null
      }));
      return { success: true, job, response };
    } catch (error) {
      const errorMessage = extractError(error, 'Failed to pause job');
      set({ loading: false, error: errorMessage });
      return { success: false, error: errorMessage };
    }
  },

  triggerMatching: async (jobId, options = {}) => {
    set({ loading: true, error: null });
    try {
      const response = await jobAPI.triggerMatching(jobId, options);
      set({ loading: false, error: null, lastMatchingResults: response.results || response });
      return { success: true, results: response.results || response };
    } catch (error) {
      const errorMessage = extractError(error, 'Failed to trigger matching');
      set({ loading: false, error: errorMessage });
      return { success: false, error: errorMessage };
    }
  },

  getImmediateMatches: async (jobId, options = {}) => {
    set({ loading: true, error: null });
    try {
      const response = await jobAPI.getImmediateMatches(jobId, cleanParams(options));
      const normalized = normalizeMatchesResponse(response);
      set({
        immediateMatches: normalized.matches,
        lastMatchingResults: normalized.statistics,
        matchesSummary: normalized.summary,
        matchesDisplayPolicy: normalized.displayPolicy,
        loading: false,
        error: null
      });
      return { success: true, matches: normalized.matches, statistics: normalized.statistics, summary: normalized.summary, response };
    } catch (error) {
      const errorMessage = extractError(error, 'Failed to get matches');
      set({ loading: false, error: errorMessage, immediateMatches: [] });
      return { success: false, error: errorMessage };
    }
  },

  fetchJobMatches: async (jobId, filters = {}) => {
    set({ loading: true, error: null });
    try {
      const response = await jobAPI.getMatches(jobId, cleanParams(filters));
      const normalized = normalizeMatchesResponse(response);
      set({
        currentJob: normalized.job || get().currentJob,
        matchingCandidates: normalized.matches,
        matchesSummary: normalized.summary,
        matchesPagination: normalized.pagination,
        matchesDisplayPolicy: normalized.displayPolicy,
        loading: false,
        error: null
      });
      return { success: true, ...normalized };
    } catch (error) {
      const errorMessage = extractError(error, 'Failed to fetch job matches');
      set({ loading: false, error: errorMessage });
      return { success: false, error: errorMessage };
    }
  },

  fetchMatchDetails: async (jobId, matchId) => {
    try {
      const response = await jobAPI.getMatchDetails(jobId, matchId);
      return { success: true, match: response.match || response };
    } catch (error) {
      return { success: false, error: extractError(error, 'Failed to fetch match details') };
    }
  },

  takeActionOnImmediateMatch: async (jobId, matchId, action, customMessage = '', options = {}) => {
    try {
      const actionPayload = {
        action,
        notifyCandidate: options.notifyCandidate ?? true
      };

      if (String(customMessage || '').trim()) {
        actionPayload.customMessage = String(customMessage).trim();
      }
      if (String(options.reason || '').trim()) {
        actionPayload.reason = String(options.reason).trim();
      }
      if (options.reachOut) {
        actionPayload.reachOut = options.reachOut;
      }

      const response = await jobAPI.takeMatchAction(jobId, matchId, actionPayload);

      const updater = (match) => ({
        ...match,
        employerAction: response.match?.employerAction || action,
        matchStatus: response.match?.status || match.matchStatus,
        actionTaken: response.match?.employerAction || action,
        candidate: {
          ...match.candidate,
          contact: response.match?.candidateContact || match.candidate?.contact
        }
      });

      set(state => ({
        immediateMatches: updateMatchInList(state.immediateMatches, matchId, updater),
        matchingCandidates: updateMatchInList(state.matchingCandidates, matchId, updater)
      }));

      return { success: true, result: response };
    } catch (error) {
      return { success: false, error: extractError(error, 'Failed to take action on match') };
    }
  },

  bulkMatchAction: async (jobId, actionData) => {
    try {
      const response = await jobAPI.bulkMatchAction(jobId, actionData);
      const successfulIds = asArray(response.results)
        .filter(result => result.success)
        .map(result => String(result.matchId));

      if (successfulIds.length) {
        const updater = (match) => successfulIds.includes(String(match.matchId))
          ? { ...match, employerAction: actionData.action, actionTaken: actionData.action }
          : match;
        set(state => ({
          immediateMatches: state.immediateMatches.map(updater),
          matchingCandidates: state.matchingCandidates.map(updater)
        }));
      }

      return { success: true, result: response };
    } catch (error) {
      return { success: false, error: extractError(error, 'Failed to take bulk action') };
    }
  },

  fetchJobProfileSetup: async (jobId) => {
    set({ loading: true, error: null });
    try {
      const response = await jobAPI.getProfileSetup(jobId);
      set({ jobProfileSetup: response, loading: false, error: null });
      return { success: true, setup: response };
    } catch (error) {
      const errorMessage = extractError(error, 'Failed to fetch job profile setup');
      set({ loading: false, error: errorMessage, jobProfileSetup: null });
      return { success: false, error: errorMessage };
    }
  },

  saveJobProfileAnswers: async (jobId, answers) => {
    set({ loading: true, error: null });
    try {
      const response = await jobAPI.saveProfileAnswers(jobId, answers);
      const setup = response?.questions
        ? {
          job: response.job,
          questions: response.questions,
          questionProgress: response.questionProgress,
          dataCoverage: response.dataCoverage
        }
        : (await get().fetchJobProfileSetup(jobId)).setup;
      set({ jobProfileSetup: setup, loading: false, error: null });
      return { success: true, response, setup };
    } catch (error) {
      const errorMessage = extractError(error, 'Failed to save job profile answers');
      set({ loading: false, error: errorMessage });
      return { success: false, error: errorMessage };
    }
  },

  fetchJobAnalytics: async (params = {}) => {
    set({ loading: true, analyticsLoading: true, error: null });
    try {
      const response = await jobAPI.getAnalytics(params);
      set({ jobAnalytics: response.analytics || response, loading: false, analyticsLoading: false, error: null });
      return { success: true, analytics: response.analytics || response };
    } catch (error) {
      const errorMessage = extractError(error, 'Failed to fetch job analytics');
      set({ loading: false, analyticsLoading: false, error: errorMessage });
      return { success: false, error: errorMessage };
    }
  },

  fetchMatchingPreferences: async () => {
    set({ loading: true, configurationLoading: true, error: null });
    try {
      const response = await jobAPI.getMatchingPreferences();
      const preferences = { ...DEFAULT_MATCHING_PREFERENCES, ...(response.preferences || response) };
      set(state => ({
        matchingPreferences: preferences,
        matchingConfiguration: {
          ...state.matchingConfiguration,
          preferences
        },
        loading: false,
        configurationLoading: false,
        error: null
      }));
      return { success: true, preferences };
    } catch (error) {
      const errorMessage = extractError(error, 'Failed to fetch matching preferences');
      set({ loading: false, configurationLoading: false, error: errorMessage });
      return { success: false, error: errorMessage };
    }
  },

  updateMatchingPreferences: async (preferences) => {
    set({ loading: true, configurationLoading: true, error: null });
    try {
      const payload = { ...DEFAULT_MATCHING_PREFERENCES, ...preferences };
      const response = await jobAPI.updateMatchingPreferences(payload);
      const savedPreferences = { ...DEFAULT_MATCHING_PREFERENCES, ...(response.preferences || payload) };
      set(state => ({
        matchingPreferences: savedPreferences,
        matchingConfiguration: {
          ...state.matchingConfiguration,
          preferences: savedPreferences
        },
        loading: false,
        configurationLoading: false,
        error: null
      }));
      return { success: true, preferences: savedPreferences };
    } catch (error) {
      const errorMessage = extractError(error, 'Failed to update matching preferences');
      set({ loading: false, configurationLoading: false, error: errorMessage });
      return { success: false, error: errorMessage };
    }
  },

  fetchMatchingConfiguration: async () => {
    set({ loading: true, configurationLoading: true, error: null });
    try {
      const response = await jobAPI.getMatchingConfiguration();
      const configuration = normalizeMatchingConfiguration(response);
      set({
        matchingConfiguration: configuration,
        matchingPreferences: configuration.preferences,
        loading: false,
        configurationLoading: false,
        error: null
      });
      return { success: true, configuration };
    } catch (error) {
      const errorMessage = extractError(error, 'Failed to fetch matching configuration');
      set({ loading: false, configurationLoading: false, error: errorMessage });
      return { success: false, error: errorMessage };
    }
  },

  updateMatchingConfiguration: async (configuration) => {
    set({ loading: true, configurationLoading: true, error: null });
    try {
      const payload = {
        preferences: {
          ...DEFAULT_MATCHING_PREFERENCES,
          ...(configuration.preferences || {})
        },
        dimensionWeights: asArray(configuration.dimensionWeights),
        categories: asArray(configuration.categories),
        microPoints: asArray(configuration.microPoints),
        ...(configuration.resetToDefaults ? { resetToDefaults: true } : {})
      };
      const response = await jobAPI.updateMatchingConfiguration(payload);
      const savedConfiguration = normalizeMatchingConfiguration(response);
      set({
        matchingConfiguration: savedConfiguration,
        matchingPreferences: savedConfiguration.preferences,
        loading: false,
        configurationLoading: false,
        error: null
      });
      return { success: true, configuration: savedConfiguration };
    } catch (error) {
      const errorMessage = extractError(error, 'Failed to update matching configuration');
      set({ loading: false, configurationLoading: false, error: errorMessage });
      return { success: false, error: errorMessage };
    }
  },

  setJobFilters: (filters) => {
    set({ jobFilters: { ...get().jobFilters, ...filters } });
  },

  setCurrentJob: (job) => set({ currentJob: job ? normalizeJob(job) : null }),
  clearImmediateMatches: () => set({ immediateMatches: [], lastMatchingResults: null }),
  clearError: () => set({ error: null }),
  resetState: () => set({
    jobs: [],
    currentJob: null,
    currentJobDetails: null,
    matchingCandidates: [],
    immediateMatches: [],
    matchesSummary: null,
    matchesPagination: null,
    matchesDisplayPolicy: null,
    lastMatchingResults: null,
    loading: false,
    jobsLoading: false,
    configurationLoading: false,
    analyticsLoading: false,
    error: null
  })
}));

export default useJobStore;
